import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, Table, Card, Modal } from "react-bootstrap";
import Pagination from "react-bootstrap/Pagination";
import Select from "react-select";
import { useAccounts } from "../../contextjs/AccountContext";
import { useTransactions } from "../../contextjs/TransactionContext";
import { fetchStoreAccountSettings } from "../../api/api"
import { Pie, Bar } from "react-chartjs-2";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Helper
const getExpenseAmount = (trx) => {
  return trx.items
    .filter(
      it =>
        it.transaction_type === "DEBIT" &&
        it.account_details?.account_type_name === "Expense" &&
        !["Cost of Goods Sold", "Purchase"].includes(it.account_details.account_name)
    )
    .reduce((acc, it) => acc + parseFloat(it.amount || 0), 0);
};

const ExpenseEntry = () => {
  const { accounts } = useAccounts();
  const {
    transactions,      // current page's expense transactions
    totalCount, // total for server pagination
    createNewTransaction,
    loadTransactions,
    fetchAllTransactions
  } = useTransactions();

  // date filter
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expenseAccountSettings, setExpenseAccountSettings] = useState([]);
  const [expensePaymentSettings, setExpensePaymentSettings] = useState([])

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 20;
  const totalPages = Math.ceil(totalCount / entriesPerPage);

  // new expense form
  const [showForm, setShowForm] = useState(false);
  // details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // local expense creation
  const [expense, setExpense] = useState({
    transaction_date: new Date().toISOString().split("T")[0],
    reference: "",
    description: "",
    payer_account: "",
    items: [{ account: "", item_description: "", amount: 0 }]
  });

  useEffect(() => {
    const loadAccountSettings = async () => {
      try {
        const [expenseSettings, paymentSettings] = await Promise.all([
          fetchStoreAccountSettings({ usage_type: "EXPENSE" }),
          fetchStoreAccountSettings({ usage_type: "EXPENSE_PAYMENT" }),
        ]);
        setExpenseAccountSettings(Array.isArray(expenseSettings) ? expenseSettings : []);
        setExpensePaymentSettings(Array.isArray(paymentSettings) ? paymentSettings : []);
      } catch (error) {
        console.error("Error loading store account settings:", error);
        setExpenseAccountSettings([]);
        setExpensePaymentSettings([]);
      }
    };

    loadAccountSettings();
  }, []);

  const enabledExpenseIds = new Set(
    expenseAccountSettings.filter((setting) => setting.is_enabled).map((setting) => String(setting.account))
  );

  const accountOptionFromSetting = (setting) => ({
    id: setting.account,
    account_name: setting.label_override || setting.account_name,
    account_type_details: {
      account_type_name: setting.account_type_name,
    },
  });

  // Payment accounts
  const basePaymentAccounts = accounts.filter(
    ac => ac.account_type_details?.account_type_name === "Asset"
  );
  const paymentAccounts = expensePaymentSettings.length
      ? expensePaymentSettings
          .filter((setting) => setting.is_enabled)
          .filter((setting) => setting.account_type_name === "Asset")
          .map(accountOptionFromSetting)
      : basePaymentAccounts;

  // Expense accounts
  const baseExpenseAccounts = accounts.filter(
    ac => ac.account_type_details.account_type_name === "Expense" &&
        !["Cost of Goods Sold", "Purchase"].includes(ac.account_name)
  );
  const expenseAccounts = expenseAccountSettings.length
      ? baseExpenseAccounts.filter((ac) => enabledExpenseIds.has(String(ac.id)))
      : baseExpenseAccounts;

  //** On changes to currentPage, date
  useEffect(() => {
    const filters = {expense_only: true};
    if (startDate) filters.start_date = startDate;
    if (endDate) filters.end_date = endDate;

    loadTransactions(currentPage, entriesPerPage, filters);
  }, [currentPage, startDate, endDate, entriesPerPage, loadTransactions]);

  //** for the charts, fetchAll in the background
  const [chartData, setChartData] = useState([]); // all expense transactions across ALL pages
  // useEffect(() => {
  //   (async () => {
  //     const filters = { expense_only: true };
  //     if (startDate) filters.start_date = startDate;
  //     if (endDate) filters.end_date = endDate;
  //
  //     const allExpenses = await fetchAllTransactions(filters);
  //     setChartData(allExpenses || []);
  //   })();
  //   // fetchAllExpenseData();
  // }, [startDate, endDate, fetchAllTransactions]);

  // handle user clicking "Load Expenses"
  const handleLoadExpenses = async () => {
    setCurrentPage(1);
    // the useEffect above will fetch page=1
    // and also triggers the fetchAllEffect for chart
    if (startDate && endDate) {
      const filters = { expense_only: true, start_date: startDate, end_date: endDate };
      const allExpenses = await fetchAllTransactions(filters);
        setChartData(allExpenses || []);
    } else {
        setChartData([]);
    }
  };

  //** New expense form logic
  const handleShowForm = () => setShowForm(true);
  const handleHideForm = () => setShowForm(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totalDebit = expense.items.reduce((acc, it) => acc + parseFloat(it.amount || 0), 0);

    const transactionItems = [
      ...expense.items.map(it => ({
        account: it.account,
        item_description: it.item_description,
        amount: parseFloat(it.amount || 0),
        transaction_type: "DEBIT"
      }))
    ];

    transactionItems.push({
      account: expense.payer_account, // Payer Account
      item_description: expense.description,
      amount: totalDebit,
      transaction_type: "CREDIT"
    });

    const newTransaction = {
      transaction_date: expense.transaction_date,
      reference: expense.reference,
      description: expense.description,
      amount: totalDebit,
      usage_context: "EXPENSE",
      items: transactionItems
    };

    try {
      await createNewTransaction(newTransaction);
    } catch (error) {
      const data = error.response?.data;
      const message = data?.items || data?.detail || "Unable to create expense transaction.";
      alert(Array.isArray(message) ? message.join("") : message);
      return;
    }

    setExpense({
      transaction_date: new Date().toISOString().split("T")[0],
      reference: "",
      description: "",
      payer_account: "",
      items: [{ account: "", item_description: "", amount: 0 }]
    });
    handleHideForm();

    // optionally re-fetch the page + chart
    const filters = { expense_only: true };
    if (startDate) filters.start_date = startDate;
    if (endDate) filters.end_date = endDate;

    loadTransactions(currentPage, entriesPerPage, filters);
    // re-fetchAll for chart
    const allExpenses = await fetchAllTransactions(filters);
    setChartData(allExpenses || []);
  };

  //** details
  const handleShowDetails = (trx) => {
    setSelectedTransaction(trx);
    setShowDetailsModal(true);
  };
  const handleCloseDetails = () => {
    setSelectedTransaction(null);
    setShowDetailsModal(false);
  };

  //** Build the chart from chartData (all pages)
  const expenseTotals = {};
  (chartData || []).forEach(trx => {
    trx.items.forEach(it => {
      if (
        it.transaction_type === "DEBIT" &&
        it.account_details?.account_type_name === "Expense" &&
        !["Cost of Goods Sold", "Purchase"].includes(it.account_details.account_name)
      ) {
        const acctName = it.account_details.account_name;
        expenseTotals[acctName] = (expenseTotals[acctName] || 0) + parseFloat(it.amount || 0);
      }
    });
  });

  const pieData = {
    labels: Object.keys(expenseTotals),
    datasets: [
      {
        data: Object.values(expenseTotals),
        backgroundColor: [
          "#FF6384","#36A2EB","#FFCE56","#9b59b6","#e74c3c","#2ecc71","#f1c40f",
          "#1abc9c","#3498db","#34495e","#16a085","#27ae60","#f39c12","#d35400",
          "#c0392b","#7f8c8d","#8e44ad","#2980b9","#2c3e50","#f39c12"
        ]
      }
    ]
  };
  const barData = {
    labels: Object.keys(expenseTotals),
    datasets: [
      {
        label: "Total Expenses",
        data: Object.values(expenseTotals),
        backgroundColor: "#36A2EB"
      }
    ]
  };
  const overallExpense = Object.values(expenseTotals).reduce((a, b) => a + b, 0);

  //** The table shows only the current page from “transactions”
  //   Filter out transactions that have 0 expense lines
  const filteredTransactions = transactions.filter(tx => getExpenseAmount(tx) > 0);

  //** handle pagination
  const handlePageChange = (newPage) => {
    if (newPage !== currentPage && newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  //** Bounded pagination
  const maxLinks = 5;
  let startLink = Math.max(currentPage - 2, 1);
  let endLink = startLink + maxLinks - 1;
  if (endLink > totalPages) {
    endLink = totalPages;
    startLink = Math.max(endLink - (maxLinks - 1), 1);
  }
  const pageNumbersToShow = [];
  for (let p = startLink; p <= endLink; p++) {
    pageNumbersToShow.push(p);
  }

  return (
    <div className="container mt-5">
      <h1>Expenses</h1>

      <Button variant="primary" className="mb-4" onClick={handleShowForm}>
        New Expense
      </Button>

      {/* Date Filter */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Group controlId="startDate">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => {setStartDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="endDate">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => {setEndDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col md={2} className="d-flex align-items-end">
          <Button variant="success" onClick={handleLoadExpenses}>
            Load Expenses
          </Button>
        </Col>
      </Row>

      {/* Chart (all pages) */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Expense Breakdown (All Pages)</Card.Title>
              <Pie data={pieData} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Expense Trend</Card.Title>
              <Bar data={barData} />
              <h2 className="text-center mt-3" style={{ fontWeight: "bold" }}>
                Total Expenses: KES {overallExpense.toLocaleString()}
              </h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h2>Expense Entries (Current Page)</h2>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Date</th>
            <th>Reference</th>
            <th>Description</th>
            <th>Payer Account</th>
            <th>Expense Amount</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredTransactions.map((trx) => {
            const amt = getExpenseAmount(trx);
            if (amt <= 0) return null;
            const payerLine = trx.items.find(
                (it) =>
                    it.transaction_type === "CREDIT" &&
                    it.account_details?.account_type_name !== "Expense"
            );
            // if (!payerLine) return null;

            // const allowedPayers = new Set([
            //     "Cash",
            //     "DTB Bank",
            //     "George Mpesa",
            //     "Kenya Commercial Bank (KCB)",
            //     "Equity Bank",
            //     "Mpesa Till Account"
            // ]);
            // if (!allowedPayers.has(payerLine.account_details.account_name)) return null;

            return (
              <tr key={trx.id}>
                <td>{new Date(trx.transaction_date).toLocaleDateString("en-GB")}</td>
                <td>{trx.reference}</td>
                <td>{trx.description}</td>
                <td>{payerLine?.account_details?.account_name || ""}</td>
                <td>{amt.toFixed(2)}</td>
                <td>
                  <Button variant="info" onClick={() => handleShowDetails(trx)}>
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Server-Side Pagination */}
      <Pagination className="justify-content-center mt-4">
        <Pagination.First
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        />
        {pageNumbersToShow.map(pg => (
          <Pagination.Item
            key={pg}
            active={pg === currentPage}
            onClick={() => handlePageChange(pg)}
          >
            {pg}
          </Pagination.Item>
        ))}
        <Pagination.Next
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>

      {/* New Expense Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>New Expense</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Similar form submission */}
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group controlId="payerAccount">
                  <Form.Label>Payer Account</Form.Label>
                  <Select
                    options={paymentAccounts.map(ac => ({
                      value: ac.id,
                      label: ac.account_name
                    }))}
                    value={
                      expense.payer_account
                        ? paymentAccounts.map(ac => ({ value: ac.id, label: ac.account_name }))
                            .find(opt => opt.value === expense.payer_account)
                        : null
                    }
                    onChange={(selected) => {
                      setExpense({ ...expense, payer_account: selected.value });
                    }}
                    placeholder="Select Payer Account"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="transactionDate">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={expense.transaction_date}
                    onChange={(e) => setExpense({ ...expense, transaction_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={6}>
                <Form.Group controlId="reference">
                  <Form.Label>Reference</Form.Label>
                  <Form.Control
                    type="text"
                    value={expense.reference}
                    onChange={(e) => setExpense({ ...expense, reference: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    value={expense.description}
                    onChange={(e) => setExpense({ ...expense, description: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Table striped bordered hover className="mt-4">
              <thead>
                <tr>
                  <th>Expense Account</th>
                  <th>Particulars</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {expense.items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Select
                        options={expenseAccounts.map(ac => ({
                          value: ac.id,
                          label: ac.account_name
                        }))}
                        value={
                          item.account
                            ? expenseAccounts.map(ac => ({ value: ac.id, label: ac.account_name }))
                                .find(opt => opt.value === item.account)
                            : null
                        }
                        onChange={(sel) => {
                          const newItems = [...expense.items];
                          newItems[index].account = sel.value;
                          setExpense({ ...expense, items: newItems });
                        }}
                        placeholder="Select Expense Account"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.item_description}
                        onChange={(e) => {
                          const newItems = [...expense.items];
                          newItems[index].item_description = e.target.value;
                          setExpense({ ...expense, items: newItems });
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={item.amount}
                        onChange={(e) => {
                          const newItems = [...expense.items];
                          newItems[index].amount = parseFloat(e.target.value || 0);
                          setExpense({ ...expense, items: newItems });
                        }}
                      />
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        onClick={() => {
                          const newItems = [...expense.items];
                          newItems.splice(index, 1);
                          setExpense({ ...expense, items: newItems });
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Button
              variant="primary"
              className="mt-3"
              onClick={() =>
                setExpense({
                  ...expense,
                  items: [...expense.items, { account: "", item_description: "", amount: 0 }]
                })
              }
            >
              + Add Line
            </Button>
            <Button
              variant="success"
              type="submit"
              className="mt-3 float-end"
            >
              Save Expense
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetails} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Expense Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTransaction && (
            <>
              <h5>
                Transaction Date:{" "}
                {new Date(selectedTransaction.transaction_date).toLocaleDateString("en-GB")}
              </h5>
              <h5>Reference: {selectedTransaction.reference}</h5>
              <h5>Description: {selectedTransaction.description}</h5>

              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Particulars</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTransaction.items.map((it, idx) => (
                    <tr key={idx}>
                      <td>{it.account_details?.account_name}</td>
                      <td>{it.item_description}</td>
                      <td>{it.transaction_type === "DEBIT" ? it.amount : ""}</td>
                      <td>{it.transaction_type === "CREDIT" ? it.amount : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ExpenseEntry;
